'use client';

import clsx from 'clsx';
import { usePathname } from 'next/navigation';
import { memo, useEffect, useMemo, useRef, useState } from 'react';

import styles from './RouteTopLoader.module.scss';
import { routeTopLoaderController, type RouteTopLoaderHandle } from './routeTopLoaderController';
import { findAnchorFromRouteClick, shouldStartRouteTopLoaderForAnchor } from './routeTopLoaderNavigation';

const TEST_ID_PREFIX = 'components-routing-route-top-loader';

export const useRouteTopLoader = (): RouteTopLoaderHandle =>
    useMemo(
        () => ({
            done: routeTopLoaderController.done,
            start: routeTopLoaderController.start,
        }),
        []
    );

const RouteTopLoaderComponent = () => {
    const pathname = usePathname();
    const routeKey = pathname || '';
    const routeKeyRef = useRef(routeKey);
    const [loaderState, setLoaderState] = useState(() => routeTopLoaderController.getSnapshot());
    const isVisible = loaderState.phase !== 'idle';

    useEffect(() => routeTopLoaderController.subscribe(setLoaderState), []);

    useEffect(() => {
        if (routeKeyRef.current === routeKey) {
            return;
        }

        routeKeyRef.current = routeKey;
        routeTopLoaderController.done();
    }, [routeKey]);

    useEffect(() => {
        const handleRouteClick = (event: MouseEvent) => {
            const anchor = findAnchorFromRouteClick(event);
            if (anchor && shouldStartRouteTopLoaderForAnchor({ anchor, event })) {
                routeTopLoaderController.start();
            }
        };

        const handlePopState = () => {
            routeTopLoaderController.start();
        };

        window.addEventListener('click', handleRouteClick, true);
        window.addEventListener('popstate', handlePopState);

        return () => {
            window.removeEventListener('click', handleRouteClick, true);
            window.removeEventListener('popstate', handlePopState);
        };
    }, []);

    return (
        <div
            aria-hidden="true"
            className={clsx(styles.root, isVisible && styles.visible)}
            data-state={loaderState.phase}
            data-testid={TEST_ID_PREFIX}
        >
            <div
                className={styles.bar}
                data-testid={`${TEST_ID_PREFIX}-bar`}
                style={{
                    transform: `scaleX(${loaderState.progress})`,
                }}
            />
        </div>
    );
};

RouteTopLoaderComponent.displayName = 'RouteTopLoader';

export const RouteTopLoader = memo(RouteTopLoaderComponent);
